import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ReactTable from "react-table";
import Modal from "components/modal";
import NoteDisplay from "components/notes/components/notes-display";
import Notes from "components/notes/contexts/notes";
import ReactTablePagination from "components/task-list/components/pagination";
import ShipmentStatus from "../shipment-status";
import Loader from "../loader/loader";
import { Chip } from "../chip/chip";

import { getColumns } from "./lib/constants";
import { useAlert } from "hooks/useAlert";
import { SettingsContext } from "contexts/settings";
import { usePagination } from "shared/hooks/use-pagination";
import { Textarea } from "../form-inputs/inputs";
import { Select } from "../form-inputs/inputs/select";
import { AdvancedFilters } from "../advanced-filters/advanced-filters";
import { AdvancedFiltersProvider } from "../../contexts/advanced-filters/advanced-filters-provider";
import { searchAdvancedFiltersFields } from "../task-list/lib/constants";
import { SHIPMENT_FILTER_TYPES } from "../shipments/lib/constants";

import { useModal } from "../../shared/hooks/use-modal";
import { faFilter, faEye } from "@fortawesome/free-solid-svg-icons";
import { useUserAdvancedFilters } from "../../shared/hooks/use-user-advanced-filters";
import { useNewSearch } from "../../shared/hooks/api/use-new-search";
import { useShipmentStatuses } from "../../shared/hooks/api/use-shipment-statuses";

import "./search-tool.scss";
import { FORM_TYPE } from "../task-list/components/form-selector";

const SearchToolIndex = () => {
    const SEARCH_ADVANCED_FILTER = "search-advanced-filter";
    const { settings } = useContext(SettingsContext);
    const { toggleAlert } = useAlert();
    const [upcs, setUpcs] = useState();
    const [upcList, setUpcList] = useState([]);
    const [showNotes, setShowNotes] = useState(false);
    const [noteSettings, setNotesSettings] = useState({});
    const [selectedRows, setSelectedRows] = useState([]);
    const [groupKey, setGroupKey] = useState("");
    const { userFilters, clearFilters, clearAdvancedFilters, externalFilters, prevExternalFilters, applyExternalFilters } =
        useUserAdvancedFilters(SEARCH_ADVANCED_FILTER);
    const { open: isAdvancedFiltersOpen, onOpen: handleOpenAdvancedFilters, onClose: handleCloseAdvancedFilters } = useModal();
    const { open: isShipmentStatusModalOpen, onOpen: handleOpenShipmentStatusModal, onClose: handleCloseShipmentStatusModal } = useModal();
    const [
        { pageNumber, pageSize, sortBy, totalCount, totalPages, searchParams, pagingParams, filteredValues },
        { setPageSize, setPage, setSortBy, setTotalCount, search, searchByUpcs, resetFilters },
    ] = usePagination({
        defaultValues: {
            sortBy: [
                {
                    id: "createDate",
                    desc: false,
                },
            ],
        },
        callback: () => {
            setUpcs(undefined);
            setUpcList([]);
        },
    });
    const { data: searchResult, mutate: searchProducts, isSuccess, isLoading } = useNewSearch(null, true);
    const { data: shipmentsUpcs, mutate: searchShipmentUpcs, isLoading: isLoadingShipmentUpcs } = useShipmentStatuses();

    const shipments = shipmentsUpcs && shipmentsUpcs.items ? shipmentsUpcs.items : [];
    const selectedUpcs = selectedRows.map(({ upc, shipmentProduct }) => `${upc}${shipmentProduct.modifier ? `-${shipmentProduct.modifier}` : ""}`);
    const groupByOptions = useMemo(
        () => [
            {
                text: "None",
                value: "",
            },
            {
                text: "UPC",
                value: "upc",
            },
            {
                text: "CES Client",
                value: "cxHClient",
            },
            {
                text: "GDN Client",
                value: "gdnClient",
            },
            {
                text: "Bin ID",
                value: "binTrackingNumber",
            },
            {
                text: "Supplier",
                value: "supplier",
            },
            {
                text: "ISDN",
                value: "internalSupplierId",
            },
            {
                text: "Shipment ID",
                value: "shipmentKey",
            },
            {
                text: "Created date",
                value: "createdOn",
            },
        ],
        [],
    );
    const optionsAssignments = {
        "shipment.type": SHIPMENT_FILTER_TYPES,
    };

    useEffect(() => {
        // don't trigger search when "upcs" is initialized
        if (upcs !== undefined) {
            searchByUpcs(upcs);
        }
    }, [upcs]);

    useEffect(() => {
        if (settings.globalNotifications.messages.length) {
            toggleAlert();
        }
    }, [settings.globalNotifications.messages.length]);

    useEffect(() => {
        if (isSuccess) {
            setUpcList(searchResult.items);
            setTotalCount(searchResult.totalRecordCount);
            setSelectedRows([]);
        }
    }, [isSuccess, searchResult]);

    // TODO: DRY, move to advanced filters hook
    useEffect(() => {
        if (searchParams && !searchParams.length && totalCount !== 0) {
            setUpcList([]);
            resetFilters();

            return;
        }

        if (searchParams.length > 0) {
            searchProducts({ searchParams, pagingParams, isUsingExternalFilters: !!externalFilters });
        }
    }, [searchParams, pagingParams]);

    useEffect(() => {
        // apply/update advanced filters
        if (externalFilters) {
            search(externalFilters);

            return;
        }

        // if advanced filters were cleared, re-run search on normal filters with stored values
        if (prevExternalFilters && !externalFilters) {
            search(userFilters);
        }
    }, [externalFilters, prevExternalFilters, externalFilters]);

    const handleShowNotes = useCallback((noteInfo) => {
        setShowNotes(true);
        setNotesSettings(noteInfo);
    }, []);

    const handleResetFilters = () => {
        resetFilters();
        clearAdvancedFilters();
        setSelectedRows([]);
    };

    const handleUpdateSelectedRow = useCallback(
        (selectedRow, isRemove) => {
            if (isRemove === "true") {
                setSelectedRows(selectedRows.filter((row) => row.key !== selectedRow.key));
            } else {
                setSelectedRows([...selectedRows, selectedRow]);
            }
        },
        [selectedRows],
    );

    const handleSelectAllRows = useCallback((selections) => {
        setSelectedRows(selections);
    }, []);

    const handleLoadUpcStatusesModal = () => {
        handleOpenShipmentStatusModal();
        searchShipmentUpcs({ shipmentKeys: selectedRows.map(({ shipment }) => shipment.key) });
    };

    const handleClearAdvancedFilters = () => {
        clearAdvancedFilters();
        setSelectedRows([]);
    };

    return (
        <div className="container">
            <h1>Search Tool</h1>
            <div className="button-group -space-between -flex-end m-b-12">
                <div>
                    <div className="flex border">
                        <Chip
                            label="Shipment Status"
                            icon={faEye}
                            disabled={selectedRows.length === 0}
                            onClick={handleLoadUpcStatusesModal}
                        />
                    </div>
                    <div
                        className="select-group"
                        style={{ display: "none" }}
                    >
                        <span>Group by:</span>
                        <Select
                            onChange={(event) => setGroupKey(event.target.value)}
                            value={groupKey}
                            excludeSelect={true}
                            id="select-all-filter"
                            options={groupByOptions}
                        />
                    </div>
                    {isAdvancedFiltersOpen && (
                        <AdvancedFiltersProvider
                            optionsAssignments={optionsAssignments}
                            filtersModel={searchAdvancedFiltersFields}
                            open={isAdvancedFiltersOpen}
                            onClose={handleCloseAdvancedFilters}
                            onFiltersSubmit={(filters) => applyExternalFilters(filters, false, handleCloseAdvancedFilters)}
                            storedFilters={userFilters}
                            clearFilters={clearFilters}
                            task={FORM_TYPE.Search}
                        >
                            <AdvancedFilters open={isAdvancedFiltersOpen} />
                        </AdvancedFiltersProvider>
                    )}
                </div>

                <div className="flex align-items-end">
                    <div>
                        <h3 className={`${!!externalFilters ? "disabled" : ""}`}>Filter By UPCs</h3>
                        <Textarea
                            className="-no-margin"
                            disabled={!!externalFilters}
                            name="upc"
                            value={upcs}
                            updateOnChange={(id, value) => setUpcs(value)}
                            disableResize={!!externalFilters}
                        />
                    </div>
                    <div className="m-l-12 m-r-12">
                        <Chip
                            disabled={isLoading}
                            label="Advanced Filters"
                            icon={faFilter}
                            onClick={handleOpenAdvancedFilters}
                            onClear={handleClearAdvancedFilters}
                            value={externalFilters && externalFilters.length}
                        />
                    </div>
                    <button
                        className="button -no-margin"
                        onClick={handleResetFilters}
                    >
                        Reset filters
                    </button>
                </div>
            </div>
            <ReactTable
                manual
                data={upcList}
                filtered={filteredValues}
                sortable
                columns={getColumns(selectedRows, handleSelectAllRows, handleUpdateSelectedRow, handleShowNotes, !externalFilters)}
                multiSort
                minRows={upcList.length > 0 ? 1 : 3}
                totalRecords={totalCount}
                showPagination
                PaginationComponent={ReactTablePagination}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                onSortedChange={setSortBy}
                onFilteredChange={search}
                sorted={sortBy}
                page={pageNumber}
                pageSize={pageSize}
                pages={totalPages}
            />

            <Modal
                show={showNotes}
                onClose={() => setShowNotes(false)}
            >
                <Notes
                    settings={{
                        shipmentKey: noteSettings.shipmentKey,
                        shipmentProductKey: noteSettings.shipmentProductKey,
                    }}
                    clientCode={noteSettings.clientCode}
                    binTrackingNumber={[noteSettings.binTrackingNumber]}
                >
                    {noteSettings.upc && <h2>{noteSettings.upc}</h2>}
                    <NoteDisplay
                        taskType=""
                        disableAdd
                        showAll
                    />
                </Notes>
            </Modal>
            <Modal
                cssClass="full-width"
                show={isShipmentStatusModalOpen}
                onClose={() => handleCloseShipmentStatusModal()}
            >
                {isLoadingShipmentUpcs ? (
                    <Loader />
                ) : (
                    <ShipmentStatus
                        products={shipments}
                        openFullUpcs={selectedUpcs}
                        showSubLabel
                    />
                )}
            </Modal>
        </div>
    );
};

export default SearchToolIndex;
